import { Colors } from '@kvika/audur-theme'
import { PrismicRichText, PrismicRichTextProps } from '@prismicio/react'
import Image from 'next/image'
import { useMediaQuery } from 'react-responsive'

import { GridItem, MainGridContainer } from '../../components/grid-container'
import { Maybe, AudurLink, CalculatorFaqSlice } from '../../prismic/prismicTypes'
import { ColumnClass, Gutters } from '../../utils/types/Types'

import styles from './FaqAnswer.module.scss'
import CTASection from './CTASection'

interface ExtendedCalculatorFaqPrimary {
	faq_title?: Maybe<string>
	faq_description?: Maybe<PrismicRichTextProps['field']>
	faq_color?: Maybe<string>
	title?: Maybe<string>
	description?: Maybe<PrismicRichTextProps['field']>
	color?: Maybe<string>
	faq_cta_title?: string
	faq_cta_description?: PrismicRichTextProps['field']
	faq_cta_link?: AudurLink
	faq_cta_link_text?: string
	faq_cta_title_2?: string
	faq_cta_description_2?: PrismicRichTextProps['field']
	faq_cta_link_2?: AudurLink
	faq_cta_link_text_2?: string
}

interface FAQField {
	faq_single_title: Maybe<string>
	faq_single_description: Maybe<PrismicRichTextProps['field']>
}

interface ExtendedCalculatorFaqSlice {
	primary: ExtendedCalculatorFaqPrimary
	fields: FAQField[]
}

const MortgageFAQSlice = ({ primary, fields }: ExtendedCalculatorFaqSlice | CalculatorFaqSlice) => {
	const isTabletOrMobile = useMediaQuery({ query: '(max-width: 979px)' })

	const getColor = (color: Maybe<string>) => {
		if (color === 'Blue') {
			return Colors.LightBlue
		}
		if (color === 'DarkGray') {
			return Colors.DarkGray
		}
		return Colors.CardGray
	}

	// Use the appropriate field names based on what's available
	const title = primary?.faq_title || primary?.title
	const description = primary?.faq_description || primary?.description
	const color = primary?.faq_color || primary?.color

	// Check if we have CTA fields - may not exist in the original CalculatorFaqPrimary
	const hasCta =
		primary &&
		'faq_cta_title' in primary &&
		'faq_cta_description' in primary &&
		primary.faq_cta_title &&
		primary.faq_cta_description

	const hasCta2 =
		primary &&
		'faq_cta_title_2' in primary &&
		'faq_cta_description_2' in primary &&
		primary.faq_cta_title_2 &&
		primary.faq_cta_description_2

	return (
		<MainGridContainer
			backgroundColor={isTabletOrMobile ? Colors.DarkGray : getColor(color || null)}
			gutters={Gutters.None}
			style={isTabletOrMobile ? { gridTemplateColumns: '0fr 1fr 0fr' } : { padding: '80px 0' }}
		>
			<GridItem className={ColumnClass.All}>
				<div className={`${styles.mortgageFAQ} ${color === 'Blue' && styles.blueBackground}`}>
					<h3>{title}</h3>
					<PrismicRichText field={description} />
					{(fields || []).map((faqItem) => {
						return (
							<div key={faqItem.faq_single_title} className={styles.answerContainer}>
								<details className={styles.answerDetails}>
									<summary>
										<div className={styles.answerHeading}>
											<span className={styles.headingText}>{faqItem.faq_single_title}</span>
											<div className={styles.plusIconWrapper}>
												<Image
													src='/svg/vextor.svg'
													alt='vextor'
													width={24}
													height={24}
													className={styles.arrowIcon}
													priority
												/>
											</div>
										</div>
									</summary>
								</details>
								<div className={styles.answersContent}>
									<PrismicRichText field={faqItem.faq_single_description as PrismicRichTextProps['field']} />
								</div>
							</div>
						)
					})}

					{/* CTA Section */}
					{hasCta && 'faq_cta_title' in primary && 'faq_cta_description' in primary && (
						<CTASection
							title={(primary as ExtendedCalculatorFaqPrimary).faq_cta_title}
							description={
								(primary as ExtendedCalculatorFaqPrimary).faq_cta_description as PrismicRichTextProps['field']
							}
							linkText={(primary as ExtendedCalculatorFaqPrimary).faq_cta_link_text || 'Verðskrá'}
							link={(primary as ExtendedCalculatorFaqPrimary).faq_cta_link}
						/>
					)}
					{hasCta2 && 'faq_cta_title_2' in primary && 'faq_cta_description_2' in primary && (
						<CTASection
							title={(primary as ExtendedCalculatorFaqPrimary).faq_cta_title_2}
							description={
								(primary as ExtendedCalculatorFaqPrimary).faq_cta_description_2 as PrismicRichTextProps['field']
							}
							linkText={(primary as ExtendedCalculatorFaqPrimary).faq_cta_link_text_2 || 'Verðskrá'}
							link={(primary as ExtendedCalculatorFaqPrimary).faq_cta_link_2}
						/>
					)}
				</div>
			</GridItem>
		</MainGridContainer>
	)
}

export default MortgageFAQSlice
